import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import styles from './MethodePage.module.css';
import { useLang } from '../../context/LanguageContext';

const steps = [
  {
    num: '01',
    title: 'Compréhension du besoin',
    desc: "Échange pour comprendre votre contexte, vos objectifs et les contraintes réelles du projet. Pas de formulaires génériques — une conversation directe pour cerner l'essentiel.",
    tag: 'Découverte',
  },
  {
    num: '02',
    title: 'Proposition de solution',
    desc: "Architecture technique, choix technologiques et estimation claire du projet. Vous savez exactement ce qui sera fait, comment et pour quel budget.",
    tag: 'Planification',
  },
  {
    num: '03',
    title: 'Design ou architecture',
    desc: "Maquettes, prototypes ou schéma d'architecture selon le type de projet — validés avec vous avant de commencer à coder.",
    tag: 'Conception',
  },
  {
    num: '04',
    title: 'Développement',
    desc: "Code propre, modulaire et documenté, avec des livraisons régulières. Vous voyez le projet avancer en temps réel, pas seulement à la fin.",
    tag: 'Réalisation',
  },
  {
    num: '05',
    title: 'Tests',
    desc: "Tests fonctionnels, corrections et validation avec vous avant la mise en ligne. Chaque fonctionnalité est vérifiée dans des conditions réelles.",
    tag: 'Validation',
  },
  {
    num: '06',
    title: 'Mise en ligne',
    desc: "Déploiement sur votre infrastructure ou sur un serveur recommandé, avec configuration complète et transfert de connaissances.",
    tag: 'Déploiement',
  },
  {
    num: '07',
    title: 'Amélioration continue',
    desc: "Suivi post-lancement, ajout de fonctionnalités, maintenance. Le projet ne s'arrête pas à la livraison — il évolue avec vos besoins.",
    tag: 'Évolution',
  },
];

const pillars = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    label: 'Livraison rapide',
    desc: 'Chaque solution est livrée dans les délais les plus courts possibles, sans sacrifier la qualité.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    label: 'Rigueur',
    desc: "Code relu, testé et structuré. Pas de raccourcis techniques qui créent des problèmes plus tard.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    ),
    label: 'Qualité mesurable',
    desc: "Des livrables clairs, documentés, qui tiennent en production et passent à l'échelle.",
  },
];

/* ── Step component — animates when in view ── */
function Step({ step, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      className={styles.step}
      initial={{ opacity: 0, x: -32, filter: 'blur(4px)' }}
      animate={inView ? { opacity: 1, x: 0, filter: 'blur(0px)' } : {}}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
    >
      {/* Left — number + connecting line */}
      <div className={styles.stepLeft}>
        <div className={styles.stepNumWrap}>
          <span className={styles.stepNum}>{step.num}</span>
        </div>
        {index < steps.length - 1 && <div className={styles.stepLine} />}
      </div>

      {/* Right — content */}
      <div className={styles.stepBody}>
        <div className={styles.stepMeta}>
          <span className={styles.stepTag}>{step.tag}</span>
        </div>
        <h3 className={styles.stepTitle}>{step.title}</h3>
        <p className={styles.stepDesc}>{step.desc}</p>
      </div>
    </motion.div>
  );
}

export default function MethodePage() {
  const pillarsRef = useRef(null);
  const pillarsInView = useInView(pillarsRef, { once: true, margin: '-60px' });

  const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } } };
  const fadeUp  = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

  const { lang } = useLang();

  const ui = {
    fr: {
      eyebrowHero: 'Méthode de travail',
      heroT1:      'Une méthode claire,',
      heroAccent:  'directe et structurée.',
      heroSub:     'En travaillant directement avec moi, vous évitez les intermédiaires : la communication est plus rapide, les décisions sont plus claires et le projet avance de manière plus fluide.',
      timeEy:      'Les 7 étapes',
      timeT1:      'De la première discussion',
      timeT2:      'à la livraison finale.',
    },
    en: {
      eyebrowHero: 'Working method',
      heroT1:      'A clear,',
      heroAccent:  'direct and structured method.',
      heroSub:     'By working directly with me, you avoid intermediaries: communication is faster, decisions are clearer and the project moves forward more smoothly.',
      timeEy:      'The 7 steps',
      timeT1:      'From the first discussion',
      timeT2:      'to the final delivery.',
    },
  }[lang];

  return (
    <div className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={styles.gridLines} />
          <div className={styles.glow} />
        </div>
        <div className={styles.heroInner}>
          <motion.p className={styles.eyebrowHero}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {ui.eyebrowHero}
          </motion.p>
          <motion.h1 className={styles.heroTitle}
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}>
            {ui.heroT1}<br />
            <span className={styles.accent}>{ui.heroAccent}</span>
          </motion.h1>
          <motion.p className={styles.heroSub}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.2 }}>
            {ui.heroSub}
          </motion.p>

          {/* Pillars strip */}
          <motion.div className={styles.pillars}
            ref={pillarsRef} variants={stagger}
            initial="hidden" animate={pillarsInView ? 'visible' : 'hidden'}>
            {pillars.map((p) => (
              <motion.div key={p.label.fr} className={styles.pillar} variants={fadeUp}>
                <div className={styles.pillarIcon}>{p.icon}</div>
                <div>
                  <p className={styles.pillarLabel}>{p.label[lang]}</p>
                  <p className={styles.pillarDesc}>{p.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className={styles.timelineSection}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.timeEy}</p>
            <h2 className={styles.sectionTitle}>{ui.timeT1}<br />{ui.timeT2}</h2>
          </div>
          <div className={styles.timeline}>
            {steps.map((step, i) => (
              <Step key={step.num} step={step} index={i} />
            ))}
          </div>
        </div>
      </section>


    </div>
  );
}
